import { integrationsMenu } from "@/app/ui/navigation/integrationsMenu";
import messages from "@/plugins/i18n";
import { computed, onMounted } from "vue";
import type { IntegrationMeta } from "@/components/pages/integrations/composables/types";
import img from "@/assets/images/app/integrations/squarespace.svg";
import { EnumIntegrationStatus } from "@/components/pages/integrations/types";
import type { SquarespaceSettings } from "@/app/services/generated/squarespacesettingsService";
import { squarespaceSettingsService } from "@/app/services/generated/squarespacesettingsService";
import {
  makeDeepRef,
  makeRef,
  withLoading,
} from "@/components/composables/componentTools";
import { showConfirmationDialog } from "@/components/composables/promptDialogs";
import router from "@/router/router";
import { BrewManError } from "@/app/errors";
import { squarespaceLinkService } from "@/app/services/generated/squarespacelinkService";
import type { SquarespaceOrder } from "@/app/services/generated/squarespaceorderService";
import {
  SquarespaceOrderFilter,
  squarespaceOrderService,
} from "@/app/services/generated/squarespaceorderService";
import { lastSixtyDays } from "@/components/controls/daterange/dateRangePresets";
import type { LocalDate } from "@js-joda/core";
import type { OrderSummary } from "@/app/services/generated/orderService";
import {
  OrderFilter,
  orderService,
} from "@/app/services/generated/orderService";
import { distinctGuids, toGuidMap } from "@/app/apiutils";
import { GuidMap } from "@/app/types/common/guidcollections";
import { fillFilterDates } from "@/store/modules/integrationErrorStore";

const callbackUrl =
  window.location.origin.toString() + "/integrations/squarespace";

const squarespaceAuthExpected = "expecting-squarespace" as const;

export function useSquarespaceIntegration() {
  const settings = makeDeepRef<SquarespaceSettings | null>(null);
  const isActivated = computed(() => integrationStatus.value.isActive());
  const isAuthenticated = makeRef(false);
  const squarespaceOrders = makeRef<readonly SquarespaceOrder[]>([]);
  const brewmanOrdersMap = makeRef(new GuidMap<OrderSummary>());
  const dateRange = makeRef(lastSixtyDays.asDateRange());

  const integrationStatus = computed(() => {
    if (!settings.value || !settings.value.outletId) {
      return EnumIntegrationStatus.inactive;
    }

    return isAuthenticated.value
      ? EnumIntegrationStatus.active
      : EnumIntegrationStatus.unauthenticated;
  });

  const meta = computed((): IntegrationMeta => {
    return {
      name: messages.squarespace_title,
      description: messages.squarespace_description,
      integrationRoute: integrationsMenu.squarespace.viewLocation(),
      miniImg: img,
      img,
      url: messages.squarespace_url,
      activated: isActivated.value,
      status: integrationStatus.value,
    };
  });

  const refresh = async () => {
    settings.value =
      await squarespaceSettingsService.getSquarespaceSettingsByTenant();

    [isAuthenticated.value] = await Promise.all([
      squarespaceSettingsService.isSquarespaceAuthenticated(settings.value.id),
      refreshOrders(),
    ]);
  };

  const refreshOrders = async () => {
    squarespaceOrders.value = await getSquarespaceOrders(
      dateRange.value.from,
      dateRange.value.to
    );

    const brewmanOrdersFilter = new OrderFilter();
    brewmanOrdersFilter.limitOrderHeaderIds = distinctGuids(
      squarespaceOrders.value.map(
        (squarespaceOrder) => squarespaceOrder.brewmanOrderId
      )
    );
    const linkedBrewmanOrders =
      await orderService.getOrderSummaries(brewmanOrdersFilter);

    brewmanOrdersMap.value = toGuidMap(
      linkedBrewmanOrders.orders,
      (order) => order.id
    );
  };

  const disconnect = async () => {
    if (
      settings.value &&
      (await showConfirmationDialog(
        messages.prompt_confirm_title,
        messages.squarespace_disconnect
      ))
    ) {
      await squarespaceSettingsService.hardDeleteSquarespaceSettings(
        settings.value.id
      );

      router.push(integrationsMenu.rootViewLocation());
    }
  };

  const beginAuth = withLoading(async (newSettings: SquarespaceSettings) => {
    await saveSettings(newSettings);

    const accountsLinkUrl =
      await squarespaceSettingsService.createSquarespaceLoginUrl(
        newSettings.id,
        callbackUrl
      );

    localStorage.setItem(squarespaceAuthExpected, "true");

    window.open(accountsLinkUrl, "_self");
  });

  const completeAuth = withLoading(
    async (queries: typeof router.currentRoute.query) => {
      if (!settings.value) {
        throw new BrewManError(
          messages.squarespace_cannot_complete_auth_with_no_settings
        );
      }

      if (!localStorage.getItem(squarespaceAuthExpected)) {
        localStorage.removeItem(squarespaceAuthExpected);
        throw new BrewManError(messages.unexpected_authentication_process);
      }
      const tempCode = queries.code;
      const state = queries.state;

      if (Array.isArray(tempCode) || Array.isArray(state)) {
        throw new BrewManError(messages.accounts_unexpected_code_state_array);
      }

      await squarespaceSettingsService.completeSquarespaceAuthentication(
        settings.value.id,
        router.currentRoute.fullPath.split("?")[1],
        callbackUrl
      );

      localStorage.removeItem(squarespaceAuthExpected);
      router.replace(integrationsMenu.squarespace.viewLocation()); // Remove query params
      await refresh();
    }
  );

  const saveSettings = withLoading(async (newSettings: SquarespaceSettings) => {
    await squarespaceSettingsService.saveSquarespaceSettings(newSettings);
  });

  const initiateOrdersImport = withLoading(async () => {
    if (settings.value) {
      await squarespaceLinkService.initiateOrderImport(settings.value.id);
    }
  });

  onMounted(withLoading(refresh));

  return {
    settings,
    meta,
    isActivated,
    squarespaceOrders,
    dateRange,
    brewmanOrdersMap,
    refreshOrders,
    refresh,
    disconnect,
    beginAuth,
    completeAuth,
    saveSettings,
    initiateOrdersImport,
  };
}

export async function getSquarespaceOrders(
  fromDate?: LocalDate | null,
  toDate?: LocalDate | null
) {
  return (
    await squarespaceOrderService.getSquarespaceOrdersByFilter(
      fillFilterDates(new SquarespaceOrderFilter(), fromDate, toDate)
    )
  ).squarespaceOrders;
}
